import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { TemplateShare } from '@horwath-htl/shared-types';

interface TemplateShareDialogProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

interface ShareRequest {
  userIds: string[];
  canEdit: boolean;
}

export const TemplateShareDialog: React.FC<TemplateShareDialogProps> = ({
  open,
  onClose,
  templateId,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [canEdit, setCanEdit] = useState(false);

  // Fetch current shares
  const {
    data: sharesData,
    isLoading: sharesLoading,
    refetch: refetchShares,
  } = useQuery({
    queryKey: ['templateShares', templateId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${templateId}/shares`
      );
      if (!response?.ok) throw new Error('Failed to fetch shares');
      return await response.json();
    },
    enabled: open,
  });

  // Fetch available users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/portal-admin/users?limit=1000');
      if (!response?.ok) throw new Error('Failed to fetch users');
      const result = await response.json();
      return result.data || [];
    },
    enabled: open,
  });

  // Add share mutation
  const addShareMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error('No user selected');

      const shareData: ShareRequest = {
        userIds: [selectedUser.id],
        canEdit,
      };

      const response = await authenticatedFetch(
        `/api/deliverables/templates/${templateId}/share`,
        {
          method: 'POST',
          body: JSON.stringify(shareData),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to add share');
      }

      return await response.json();
    },
    onSuccess: () => {
      refetchShares();
      setSelectedUser(null);
      setCanEdit(false);
      // Invalidate all related queries to ensure parent components refresh
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateShares', templateId] });
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
    },
  });

  // Remove share mutation
  const removeShareMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${templateId}/shares/${userId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to remove share');
      }

      return await response.json();
    },
    onSuccess: () => {
      refetchShares();
      // Invalidate all related queries to ensure parent components refresh
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templateShares', templateId] });
      queryClient.invalidateQueries({ queryKey: ['template', templateId] });
    },
  });

  const shares: TemplateShare[] = sharesData?.shares || [];
  const users: User[] = usersData || [];

  // Filter out users who already have access
  const sharedUserIds = shares.map((share) => share.user_id);
  const availableUsers = users.filter((user) => !sharedUserIds.includes(user.id));

  // DataGrid columns for shares
  const columns: GridColDef[] = [
    {
      field: 'user_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'user_email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'can_edit',
      headerName: 'Can Edit',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          color="error"
          onClick={() => removeShareMutation.mutate(params.row.user_id)}
          disabled={removeShareMutation.isPending}
        >
          Remove
        </Button>
      ),
    },
  ];

  const handleAddShare = () => {
    addShareMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Share Template</DialogTitle>
      <DialogContent>
        {/* Current Shares */}
        <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
          Current Shares
        </Typography>
        <Paper variant="outlined" sx={{ mb: 3, height: 300 }}>
          {sharesLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : shares.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <Typography variant="body2" color="text.secondary">
                This template is not currently shared with anyone
              </Typography>
            </Box>
          ) : (
            <DataGrid
              rows={shares}
              columns={columns}
              getRowId={(row) => row.user_id}
              disableRowSelectionOnClick
              hideFooter
              sx={{ border: 'none' }}
            />
          )}
        </Paper>

        {/* Add New Share */}
        <Typography variant="h6" gutterBottom>
          Add New Share
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Autocomplete
            options={availableUsers}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={selectedUser}
            onChange={(event, value) => setSelectedUser(value)}
            disabled={usersLoading || addShareMutation.isPending}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                placeholder="Search users..."
              />
            )}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={canEdit}
                onChange={(e) => setCanEdit(e.target.checked)}
                disabled={addShareMutation.isPending}
              />
            }
            label={
              <Typography variant="body2">
                Can Edit - Allow this user to modify template metadata
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleAddShare}
              disabled={!selectedUser || addShareMutation.isPending}
              startIcon={addShareMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {addShareMutation.isPending ? 'Adding...' : 'Add Share'}
            </Button>
          </Box>

          {addShareMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {addShareMutation.error instanceof Error
                ? addShareMutation.error.message
                : 'Failed to add share'}
            </Alert>
          )}

          {removeShareMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {removeShareMutation.error instanceof Error
                ? removeShareMutation.error.message
                : 'Failed to remove share'}
            </Alert>
          )}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
